import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from '../../context/LanguageContext';

export default function AboutSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });
  const { t } = useLanguage();

  return (
    <section
      ref={ref}
      id="acerca"
      className="relative overflow-hidden"
      style={{
        background: '#000',
        paddingTop: '10rem',
        paddingBottom: '5rem',
      }}
    >
      {/* Radial gradient overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at top, rgba(141,194,31,0.04) 0%, transparent 65%)' }}
      />

      <div className="v-container relative z-10">
        {/* Label */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-white/40 text-sm tracking-widest uppercase mb-8"
        >
          {t('about.label')}
        </motion.p>

        {/* Main heading */}
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="text-4xl md:text-6xl lg:text-7xl text-white leading-[1.1] tracking-tight mb-16"
        >
          {t('about.h1a')}{' '}
          <em style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', color: 'rgba(255,255,255,0.55)' }}>{t('about.h1b')}</em>{' '}
          {t('about.h1c')}
          <br className="hidden md:block" />
          {' '}{t('about.h1d')}{' '}
          <em style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', color: 'rgba(255,255,255,0.55)' }}>{t('about.h1e')}</em>
          <br />
          <em style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', color: 'rgba(255,255,255,0.55)' }}>{t('about.h1f')}</em>
        </motion.h2>

        {/* Two columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.25 }}
          >
            <p className="text-base md:text-lg text-white/65 leading-relaxed mb-6">{t('about.p1')}</p>
            <div className="flex flex-wrap gap-2">
              {[t('about.pill1'), t('about.pill2'), t('about.pill3')].map((pill) => (
                <span
                  key={pill}
                  className="text-xs font-semibold px-3 py-1.5 rounded-full"
                  style={{ background: 'rgba(141,194,31,0.08)', border: '1px solid rgba(141,194,31,0.2)', color: '#8DC21F' }}
                >
                  {pill}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.4 }}
          >
            <p className="text-base md:text-lg text-white/65 leading-relaxed">{t('about.p2')}</p>
            <div className="mt-8 gradient-line w-24" />
          </motion.div>
        </div>

        {/* Photo strip */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="grid grid-cols-3 gap-3 mt-14"
        >
          {[
            { url: 'https://images.unsplash.com/photo-1763060893376-c95428e0a569?w=600&q=80', alt: 'Street cleaning operations' },
            { url: 'https://images.unsplash.com/photo-1622322977781-76f2247964b7?w=600&q=80', alt: 'Waste treatment plant' },
            { url: 'https://images.pexels.com/photos/35280311/pexels-photo-35280311.jpeg?w=600&auto=compress', alt: 'Advanced robotics technology' },
          ].map((img, i) => (
            <motion.div
              key={i}
              whileHover={{ scale: 1.02 }}
              className="rounded-2xl overflow-hidden aspect-[4/3]"
              style={{ transition: 'transform 0.3s' }}
            >
              <img
                src={img.url}
                alt={img.alt}
                className="w-full h-full object-cover"
                style={{ filter: 'brightness(0.7) saturate(0.9)' }}
              />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
